import { createServerError } from "./apiError";
import { resolveApiBaseUrl } from "./apiBase";

// Maintenance API service for admin dashboard

interface MaintenanceResponse {
    success: boolean;
    data?: any;
    message?: string;
    timestamp?: string;
}


interface SystemInfoResponse extends MaintenanceResponse {
    data: {
        nodeVersion: string;
        platform: string;
        architecture: string;
        memory: {
            used: number;
            total: number;
            percentage: number;
            free: number;
        };
        cpu: {
            usage: number;
            loadAverage: number[];
            cores: number;
        };
        uptime: {
            process: number;
            system: number;
        };
    };
}


interface EnvironmentInfoResponse extends MaintenanceResponse {
    data: {
        frontend: {
            url: string;
            port: number;
            environment: string;
            version: string;
        };
        api: {
            url: string;
            port: number;
            environment: string;
            version: string;
        };
        databases: {
            mongodb: {
                status: 'connected' | 'disconnected' | 'unknown';
                host?: string;
                port?: number;
                database?: string;
            };
            firebaseAuth: {
                status: 'connected' | 'disconnected' | 'unknown';
                projectId?: string;
                authDomain?: string;
                emulator?: {
                    enabled: boolean;
                    authHost?: string;
                };
            };
        };
        proxy: {
            collections: string;
            apiDocs: string;
        };
    };
}

interface BackupUsersResponse extends MaintenanceResponse {
    data: {
        backupStats: {
            totalUsers: number;
            backedUpUsers: number;
            skippedUsers: number;
            errors: string[];
        };
    };
}

const API_URL = resolveApiBaseUrl();

// Get auth token from localStorage
const getAuthToken = (): string | null => {
    return localStorage.getItem('idToken');
};

// Headers for API requests
const getHeaders = (includeContentType: boolean = true, includeAuth: boolean = true) => {
    const headers: Record<string, string> = {};

    // Only add Content-Type for requests with body (POST, PUT, etc.)
    if (includeContentType) {
        headers['Content-Type'] = 'application/json';
    }

    // Add Bearer token from localStorage
    if (includeAuth) {
        const token = getAuthToken();
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
    }

    return headers;
};

class MaintenanceAPI {
    // System information endpoint - use dedicated /health/system endpoint
    async getSystemInfo(): Promise<SystemInfoResponse> {
        try {
            const url = API_URL ? `${API_URL}/health/system` : `/health/system`;
            const response = await fetch(url, {
                method: 'GET',
                headers: getHeaders(false)
            });

            const responseText = await response.text();

            if (!response.ok) {
                if (response.status >= 500) {
                    throw createServerError(response.status);
                }
                throw new Error(`HTTP ${response.status}: ${responseText}`);
            }

            const data = JSON.parse(responseText);

            // Return the actual system info from the API
            return data;

        } catch (error) {
            console.error('❌ System info check failed:', error);
            throw error;
        }
    }

    // Clear cache endpoint - use real /maintenance/cache/clear endpoint
    async clearCache(): Promise<MaintenanceResponse> {
        try {
            const url = API_URL ? `${API_URL}/maintenance/cache/clear` : `/maintenance/cache/clear`;
            const response = await fetch(url, {
                method: 'POST',
                headers: getHeaders(false, true) // Include auth for this endpoint
            });

            const responseText = await response.text();

            if (!response.ok) {
                if (response.status >= 500) {
                    throw createServerError(response.status);
                }
                throw new Error(`HTTP ${response.status}: ${responseText}`);
            }

            const data = JSON.parse(responseText);
            return data;

        } catch (error) {
            console.error('❌ Clear cache failed:', error);
            throw error;
        }
    }

    // Restart services endpoint - use real /maintenance/services/restart endpoint
    async restartServices(): Promise<MaintenanceResponse> {
        try {
            const url = API_URL ? `${API_URL}/maintenance/services/restart` : `/maintenance/services/restart`;
            const response = await fetch(url, {
                method: 'POST',
                headers: getHeaders(false, true) // Include auth for this endpoint
            });

            const responseText = await response.text();

            if (!response.ok) {
                if (response.status >= 500) {
                    throw createServerError(response.status);
                }
                throw new Error(`HTTP ${response.status}: ${responseText}`);
            }

            const data = JSON.parse(responseText);
            return data;

        } catch (error) {
            console.error('❌ Restart services failed:', error);
            throw error;
        }
    }

    // Backup users to MongoDB - use real /auth/admin/backup-users endpoint
    async backupUsers(): Promise<BackupUsersResponse> {
        try {
            const url = API_URL ? `${API_URL}/auth/admin/backup-users` : `/auth/admin/backup-users`;
            const response = await fetch(url, {
                method: 'POST',
                headers: getHeaders(false, true) // Include auth for this endpoint
            });

            const responseText = await response.text();

            if (!response.ok) {
                if (response.status >= 500) {
                    throw createServerError(response.status);
                }
                throw new Error(`HTTP ${response.status}: ${responseText}`);
            }

            const data = JSON.parse(responseText);
            return data;

        } catch (error) {
            console.error('❌ Backup users failed:', error);
            throw error;
        }
    }

    // Get environment information - client-side compilation of environment data
    async getEnvironmentInfo(): Promise<EnvironmentInfoResponse> {
        try {
            // Get current frontend URL and port
            const currentUrl = window.location.origin;
            const currentPort = parseInt(window.location.port) || (window.location.protocol === 'https:' ? 443 : 80);

            // Determine API URL
            const apiUrl = API_URL || 'http://localhost:3000';
            const apiPort = API_URL ? new URL(API_URL).port : '3000';

            // Get Firebase configuration
            const firebaseProjectId = import.meta.env.VITE_FIREBASE_PROJECT_ID || 'labwick-a71d9';
            const authEmulatorHost = import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_HOST || 'http://localhost:9099';
            const useAuthEmulator = import.meta.env.VITE_USE_FIREBASE_AUTH_EMULATOR === 'true';

            const environmentInfo: EnvironmentInfoResponse = {
                success: true,
                data: {
                    frontend: {
                        url: currentUrl,
                        port: currentPort,
                        environment: import.meta.env.VITE_NODE_ENV || import.meta.env.MODE || 'development',
                        version: import.meta.env.VITE_APP_VERSION || '1.0.0'
                    },
                    api: {
                        url: apiUrl,
                        port: parseInt(apiPort) || 3000,
                        environment: 'development', // This would come from the API in a real implementation
                        version: '1.0.0' // This would come from the API in a real implementation
                    },
                    databases: {
                        mongodb: {
                            status: 'unknown', // This would be determined by the API
                            host: 'localhost',
                            port: 27017,
                            database: 'labwick'
                        },
                        firebaseAuth: {
                            status: 'connected',
                            projectId: firebaseProjectId,
                            authDomain: `${firebaseProjectId}.firebaseapp.com`,
                            emulator: {
                                enabled: useAuthEmulator,
                                authHost: useAuthEmulator ? authEmulatorHost : undefined
                            }
                        }
                    },
                    proxy: {
                        collections: `${currentUrl}/collections → ${apiUrl}/collections`,
                        apiDocs: `${currentUrl}/api-docs → ${apiUrl}/api-docs`
                    }
                },
                timestamp: new Date().toISOString()
            };

            return environmentInfo;

        } catch (error) {
            console.error('❌ Failed to get environment info:', error);
            throw error;
        }
    }
}

export const maintenanceAPI = new MaintenanceAPI();
export default maintenanceAPI;
export type { SystemInfoResponse, MaintenanceResponse, EnvironmentInfoResponse, BackupUsersResponse };
